export interface ParsedCsv {
  headers: string[];
  rows: Array<Record<string, string | undefined>>;
}

const detectDelimiter = (line: string): ',' | ';' => {
  const commaCount = (line.match(/,/g) || []).length;
  const semicolonCount = (line.match(/;/g) || []).length;
  return semicolonCount > commaCount ? ';' : ',';
};

const parseLine = (line: string, delimiter: ',' | ';'): string[] => {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const next = line[i + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        i++; // skip escaped quote
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === delimiter && !inQuotes) {
      values.push(current);
      current = '';
      continue;
    }

    current += char;
  }

  values.push(current);
  return values;
};

export const parseCsv = (text: string): ParsedCsv => {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trimEnd())
    .filter((line) => line.length > 0);

  if (lines.length === 0) {
    return { headers: [], rows: [] };
  }

  const delimiter = detectDelimiter(lines[0]);
  const headers = parseLine(lines[0], delimiter).map((h) => h.trim());

  const rows = lines.slice(1).map((line) => {
    const columns = parseLine(line, delimiter);
    return headers.reduce<Record<string, string | undefined>>(
      (acc, header, idx) => {
        const raw = columns[idx] ?? '';
        const trimmed = raw.trim();
        acc[header] = trimmed === '' ? undefined : trimmed;
        return acc;
      },
      {}
    );
  });

  const nonEmptyRows = rows.filter((row) =>
    Object.values(row).some((value) => value !== undefined)
  );

  return { headers, rows: nonEmptyRows };
};
